import React, { useEffect } from 'react';

import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Placeholder,
    Highlight,
    FeatureItem, FeatureTitle, FeaturesGrid,
    HeroSection,
    PageTitle,
    USPSection, USPTitle, USPDescription, FigureWrapper, FigureCaption
} from '../components/ui/PageElements';
import {
    AudienceItem, AudienceList,
    FeatureList, FeatureListItem, RoadmapListItem, StyledList
} from "../components/ui/ListStyles.jsx";
import ResponsiveImage from '../components/ui/ResponsiveImage';
import ResponsiveVideo from '../components/ui/ResponsiveVideo';
import PageMeta from '../components/PageMeta';

const PukaPokPage = () => {
    // Page-specific preload for LCP hero image
    useEffect(() => {
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.type = 'image/webp';
        preloadLink.href = '/assets/Pukapok/img/shelves-800w.webp';
        preloadLink.imageSrcset = '/assets/Pukapok/img/shelves-200w.webp 200w, /assets/Pukapok/img/shelves-300w.webp 300w, /assets/Pukapok/img/shelves-400w.webp 400w, /assets/Pukapok/img/shelves-800w.webp 800w, /assets/Pukapok/img/shelves-1200w.webp 1200w, /assets/Pukapok/img/shelves-1504w.webp 1504w';
        preloadLink.imageSizes = '(max-width: 550px) 100vw, 550px';
        document.head.appendChild(preloadLink);

        return () => {
            document.head.removeChild(preloadLink);
        };
    }, []);

    return (
        <>
            <PageMeta
                title="PukaPok"
                description="PukaPok - A modern PDF library and viewer. Smart organization, comfortable reading modes, and unique tools for researchers and professionals."
                canonicalPath="/projects/pukapok"
            />
            {/* --- Hero Section --- */}
            <HeroSection style={{ paddingBottom: '25px' }}>
                <PageTitle>PukaPok</PageTitle>
                <SubHeadline style={{ fontStyle: 'italic', marginBottom: '10px' }}>
                    Organize, Read, and Understand PDFs Intelligently.
                </SubHeadline>
                <SubHeadline>
                    A modern, open-source PDF library manager and viewer designed for clarity, speed, and powerful features. Organize thousands of documents effortlessly, enjoy a comfortable reading experience, and unlock deeper understanding with unique tools built for researchers and professionals.
                </SubHeadline>
                <FigureWrapper style={{ marginTop: '10px', marginBottom: '30px' }}>
                    <div style={{ display: 'block', width: '550px', maxWidth: '100%', margin: '0 auto' }}>
                        <ResponsiveImage
                            src="/assets/Pukapok/img/shelves.gif"
                            alt="Animated GIF showing different bookshelves being selected in PukaPok (All, Dev, Origami, Philo, Manual), displaying the organized PDF contents of each shelf."
                            loading="eager"
                            fetchPriority="high"
                            sizes="(max-width: 600px) 100vw, 500px"
                            style={{ display: 'block' }}
                        />
                    </div>
                    <FigureCaption>
                        Say goodbye to messy folders. Organize your documents intuitively with dedicated bookshelves for different subjects or projects.
                    </FigureCaption>
                </FigureWrapper>
                <StyledButton disabled>Coming Soon!</StyledButton>
            </HeroSection>

            {/* --- Problem/Solution Section --- */}
            <Section style={{ paddingTop: '25px' }}>
                <SectionTitle>Stop Fighting Your PDFs</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    Remember the frustration of disorganized PDF folders? Struggling with outdated interfaces, sluggish viewers, and the lack of proper dark modes? Finding specific information buried in mountains of documents can feel impossible.
                    <br /><br />
                    <Highlight>PukaPok is the Answer.</Highlight> I built PukaPok from the ground up, inspired by the need for a truly modern solution. It combines elegant library management with a sophisticated, comfortable viewer, packed with smart features you won't find elsewhere.
                </SubHeadline>
            </Section>

            {/* --- Key Features Section --- */}
            <Section>
                <SectionTitle>Everything You Need</SectionTitle>
                <FeaturesGrid>
                    <FeatureItem>
                        <FeatureTitle>Organize with Ease</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Intuitive Bookshelves:</Highlight> Group your documents logically.</FeatureListItem>
                            <FeatureListItem><Highlight>Flexible Tagging:</Highlight> Add multiple tags for powerful cross-referencing.</FeatureListItem>
                            <FeatureListItem><Highlight>Simple Import:</Highlight> Drag-and-drop or use the import button.</FeatureListItem>
                            <FeatureListItem><Highlight>Metadata Editor:</Highlight> Quickly fix titles and authors directly within PukaPok.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/Pukapok/img/metadata.png"
                                alt="Screenshot of PukaPok's library view showing the metadata editor side panel alongside the separate tag manager window."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>
                                Effortlessly manage your library: Edit PDF metadata directly, assign tags, and organize your entire tag structure with the dedicated manager.
                            </FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Read in Comfort</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Fluid Viewing:</Highlight> Smooth scrolling and zooming, even with large files, thanks to multi-phase rendering and advanced caching.</FeatureListItem>
                            <FeatureListItem><Highlight>Multiple Layouts:</Highlight> Choose single-page or two-page spreads, both with continuous scrolling available.</FeatureListItem>
                            <FeatureListItem><Highlight>Eye-Friendly Modes:</Highlight> Unique <Highlight>true dark mode</Highlight> (inverse colors) or a gentle <Highlight>sepia tone</Highlight>.</FeatureListItem>
                            <FeatureListItem><Highlight>Smart Preloading:</Highlight> Anticipates your reading for a seamless experience.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/Pukapok/img/viewModes.gif"
                                alt="Animated GIF demonstrating PukaPok viewer modes: cycling through normal, true dark, and sepia themes in both single-page and dual-page layouts."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>
                                Switch seamlessly between single or dual-page layouts, and choose your comfort: standard view, true dark mode, or a gentle sepia tone. Reading PDFs your way.
                            </FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Find Anything, Instantly</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Fast Search:</Highlight> Locate documents in milliseconds (designed for 10,000+ files).</FeatureListItem>
                            <FeatureListItem><Highlight>Comprehensive Indexing:</Highlight> Search filenames, titles, metadata, tags, and even <Highlight>bookmark comments</Highlight>!</FeatureListItem>
                            <FeatureListItem><Highlight>Advanced Logic:</Highlight> Multi-phase search (exact, compound, fuzzy) finds what you mean.</FeatureListItem>
                            <FeatureListItem><Highlight>(Optional) Semantic Search:</Highlight> Leverage a <Highlight>local LLM</Highlight> for meaning-based search (Your data stays private!).</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/Pukapok/img/search.png"
                                alt="Screenshot of PukaPok search results displaying documents found for the query 'programming', highlighting the search term within the results list."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>
                                Find precisely what you need. PukaPok's powerful search digs through content, metadata, tags, and even bookmark comments, you don't even need to type correctly !
                            </FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Powerful Bookmarking</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Annotated Bookmarks:</Highlight> Add comments and see page previews directly in your bookmark list.</FeatureListItem>
                            <FeatureListItem><Highlight>Searchable Notes:</Highlight> Your bookmark comments become part of the searchable library index.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/Pukapok/img/BookmarkEditor.png"
                                alt="Screenshot of PukaPok viewer showing the bookmark side panel open with the bookmark editor window displayed for adding comments and previews."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>
                                Go beyond simple page marking. Add detailed comments and see page previews in your bookmarks – all fully searchable alongside your document content.
                            </FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>

                    <FeatureItem>
                        <FeatureTitle>Advanced Reference System</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Smart Hover:</Highlight> Instantly preview equations, figures, and tables by hovering over citations.</FeatureListItem>
                            <FeatureListItem><Highlight>Pinning Power:</Highlight> Pin any preview to keep it visible while you read.</FeatureListItem>
                            <FeatureListItem><Highlight>Custom Pins:</Highlight> Select and pin <Highlight>any rectangular area</Highlight> of the PDF for reference.</FeatureListItem>
                            <FeatureListItem><Highlight>Organized Workflows:</Highlight> Group your pins to show or hide entire sets of information as needed.</FeatureListItem>
                        </FeatureList>
                        <FigureWrapper>
                            <ResponsiveImage
                                src="/assets/Pukapok/img/refSystem.png"
                                alt="Screenshot demonstrating the reference system: a pinned figure, a hovering equation preview, and the pin manager sidebar."
                                sizes="(max-width: 600px) 100vw, 300px"
                            />
                            <FigureCaption>
                                Never lose your place. Preview references on hover, pin critical figures or equations, and organize them into groups for complex study sessions.
                            </FigureCaption>
                        </FigureWrapper>
                    </FeatureItem>
                </FeaturesGrid>
            </Section>

            {/* --- Unique Selling Point Section --- */}
            <USPSection>
                <USPTitle>A Game-Changer for Scientific Reading</USPTitle>
                <USPDescription>
                    Reading research papers and technical documents just got <Highlight>significantly</Highlight> easier. PukaPok's standout feature automatically identifies references to:
                    <br /> <Highlight>Equations, Figures, and Tables.</Highlight>
                    <br /><br />
                    Simply <Highlight>hover over a reference citation</Highlight> (e.g., "(12)", "Fig. 3") in the text, and PukaPok instantly displays the referenced item in a convenient pop-up window! No more endless scrolling back and forth. Stay focused and understand complex documents faster.
                </USPDescription>
                <FigureWrapper>
                    <ResponsiveVideo
                        src="/assets/Pukapok/video/pukapokRefAndPinsWorkflow.mp4"
                        alt="Video demonstrating PukaPok's reference hover-preview system, pinning, and pin groups"
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls={false}
                        clickToPause={false}
                        linkToFullRes={true}
                    />
                    <FigureCaption>
                        Hover over citations to instantly preview figures, tables, and equations. Pin important references for quick access and organize them into groups for a streamlined workflow.
                    </FigureCaption>
                </FigureWrapper>
            </USPSection>

            {/* --- Performance & Design --- */}
            <Section>
                <SectionTitle>User-Focused Tools That Work</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    PukaPok follows a core philosophy: provide a clean, intuitive, and aesthetically pleasing interface without sacrificing power or performance. Every feature is designed with your workflow in mind.
                </SubHeadline>
                <FeatureList style={{ maxWidth: '900px', margin: '0 auto 40px auto' }}>
                    <FeatureListItem><Highlight>Lightweight & Fast:</Highlight> Built with efficiency using Python and Qt.</FeatureListItem>
                    <FeatureListItem><Highlight>Scalable:</Highlight> Designed to handle massive libraries and large PDFs smoothly.</FeatureListItem>
                    <FeatureListItem><Highlight>User-Focused:</Highlight> Crafted to enhance your reading and organization workflow.</FeatureListItem>
                </FeatureList>
                <FigureWrapper>
                    <ResponsiveImage
                        src="/assets/Pukapok/img/pukapokEx.png"
                        alt="PukaPok viewer in dual-page mode showing a research paper with a pinned figure for easy reference while reading."
                        sizes="(max-width: 800px) 100vw, 800px"
                    />
                    <FigureCaption>
                        Dual-page view for comfortable reading, pinned figures for instant reference, bookmarks for navigation. PukaPok combines the tools you need without the clutter you don't.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            {/* --- Target Audience --- */}
            <Section>
                <SectionTitle>Who is PukaPok For?</SectionTitle>
                <AudienceList style={{ maxWidth: '900px', margin: '0 auto' }}>
                    <AudienceItem><strong>Researchers & Academics:</strong> Leverage the unique reference linking and powerful search.</AudienceItem>
                    <AudienceItem><strong>Students:</strong> Organize course materials and research papers effectively.</AudienceItem>
                    <AudienceItem><strong>Professionals:</strong> Manage technical manuals, reports, and documentation with ease.</AudienceItem>
                    <AudienceItem><strong>Anyone with a large PDF library:</strong> Finally tame your digital documents!</AudienceItem>
                    <AudienceItem><strong>Dark Mode Enthusiasts:</strong> Enjoy a comfortable, truly dark reading experience.</AudienceItem>
                </AudienceList>
            </Section>

            {/* --- Status & Future --- */}
            <Section>
                <SectionTitle>Status & Roadmap</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    PukaPok is currently in its final stages of preparation for release, though it still requires significant refactoring, code cleaning, and fine-tuning of complex systems. As this is a "side-side project," its completion might take some time, but rest assured, the core is nearly ready for an initial release!
                </SubHeadline>
                <StyledList style={{ maxWidth: '900px', margin: '0 auto 40px auto' }}>
                    <RoadmapListItem><Highlight>Availability:</Highlight> Soon for <Highlight>Windows</Highlight>, <Highlight>Linux</Highlight> and <Highlight>macOS</Highlight>(via download & GitLab).</RoadmapListItem>
                    <RoadmapListItem><Highlight>License:</Highlight> Planned to be <Highlight>Open Source</Highlight>.</RoadmapListItem>
                    <RoadmapListItem><Highlight>Current Focus:</Highlight> Refactoring & Cleaning, Fine-tuning existing features, especially reference linking.</RoadmapListItem>
                    <RoadmapListItem><Highlight>Roadmap Includes:</Highlight> PDF Annotation Tools, Bibliography Reference Linking, Enhanced AI Indexing, RAG Capabilities, Support for EPUB/Markdown/HTML.</RoadmapListItem>
                </StyledList>
            </Section>

            {/* --- Final CTA --- */}
            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Get Ready for a Better PDF Experience</SectionTitle>
                <SubHeadline>
                    Follow the project on GitLab to be notified when PukaPok v1.0 is released!
                </SubHeadline>
                {/* Replace # with your actual GitHub link when ready */}
                <StyledButton disabled>
                    Coming Soon!
                </StyledButton>
                <br /><br />
                <SubHeadline>
                    Or if it's urgent, or if you just want to share your interest in the project, feel free to reach out.
                </SubHeadline>
                <StyledButton to="/contact">
                    Contact Me
                </StyledButton>
                {/* Add a disabled download button for now */}
                {/*<StyledButton disabled>Download (Coming Soon)</StyledButton>*/}
            </Section>

        </>
    );
};

export default PukaPokPage;
